import React, { useState, useRef } from 'react';
import { Input } from './ui/input';

/**
 * TimeInput — a text input that accepts typed time values and normalizes them.
 * Accepts: "7:30 AM", "730am", "0730", "14:00", "2pm", "2:00 PM", etc.
 * Stores value in 24h format "HH:MM" for the backend.
 */
export default function TimeInput({ value, onChange, placeholder, className, ...props }) {
  const [displayValue, setDisplayValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  // Convert 24h "HH:MM" to display "h:MM AM/PM"
  const to12h = (val) => {
    if (!val) return '';
    const parts = val.split(':');
    if (parts.length !== 2) return val;
    let h = parseInt(parts[0], 10);
    const m = parts[1];
    if (isNaN(h)) return val;
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${h}:${m} ${ampm}`;
  };

  // Parse various time formats into 24h "HH:MM"
  const parseTime = (input) => {
    if (!input) return '';
    const s = input.trim().toLowerCase().replace(/\s+/g, ' ');
    
    // Try "H:MM AM/PM" or "HH:MM AM/PM"
    let match = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
    if (match) {
      let h = parseInt(match[1], 10);
      const m = parseInt(match[2], 10);
      const ampm = match[3];
      if (ampm === 'pm' && h < 12) h += 12;
      if (ampm === 'am' && h === 12) h = 0;
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }

    // Try "Ham/pm" or "HHam/pm" (no minutes)
    match = s.match(/^(\d{1,2})\s*(am|pm)$/i);
    if (match) {
      let h = parseInt(match[1], 10);
      const ampm = match[2];
      if (ampm === 'pm' && h < 12) h += 12;
      if (ampm === 'am' && h === 12) h = 0;
      if (h >= 0 && h <= 23) {
        return `${String(h).padStart(2, '0')}:00`;
      }
    }

    // Try "HHMM" (military, no colon)
    match = s.match(/^(\d{3,4})$/);
    if (match) {
      const num = match[1].padStart(4, '0');
      const h = parseInt(num.slice(0, 2), 10);
      const m = parseInt(num.slice(2), 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }

    // Try "HH:MM" (24h)
    match = s.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      const h = parseInt(match[1], 10);
      const m = parseInt(match[2], 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }

    return '';
  };

  const handleFocus = () => {
    setFocused(true);
    setDisplayValue(value ? to12h(value) : '');
  };

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseTime(displayValue);
    if (parsed) {
      onChange(parsed);
    } else if (displayValue === '') {
      onChange('');
    }
    setDisplayValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      value={focused ? displayValue : (value ? to12h(value) : '')}
      onChange={(e) => setDisplayValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder || 'e.g. 7:30 AM'}
      className={className}
      {...props}
    />
  );
}
